import React, { type RefObject } from "react";
import { ProvetCancelBookingModal } from "../components/modals/provetCancelBookingModal";
import { ProvetCancellationFailureModal } from "../components/modals/provetCancellationFailureModal";
import { ProvetCancellationSuccessModal } from "../components/modals/provetCancellationSuccessModal";
import { ProvetConfirmCloseModal } from "../components/modals/provetConfirmCloseModal";
import { ProvetSummaryModal } from "../components/modals/provetSummaryModal";
import { ProvetTreatmentModal } from "../components/modals/provetTreatmentModal";
import { CancellationDetails, ProvetDialogStep, ProvetFormData } from "../types";

/**
 * Modal state interface from useProvetModals hook.
 */
interface ModalState {
  summary: {
    active: boolean;
    close: () => void;
  };
  confirmClose: {
    active: boolean;
    close: () => void;
  };
  treatment: {
    active: boolean;
    title: string;
    content: string;
    close: () => void;
  };
  cancelBooking: {
    active: boolean;
    close: () => void;
  };
  cancelSuccess: {
    active: boolean;
    details: CancellationDetails | null;
    close: () => void;
  };
  cancelFailure: {
    active: boolean;
    details: CancellationDetails | null;
    close: () => void;
  };
}

/**
 * Scroll refs interface from useProvetScrollManagement hook.
 */
interface ScrollRefs {
  treatmentScrollIndicatorRef: RefObject<HTMLDivElement>;
  treatmentModalContentRef: RefObject<HTMLDivElement>;
  treatmentModalRef: RefObject<HTMLDivElement>;
  summaryScrollIndicatorRef: RefObject<HTMLDivElement>;
  summaryModalContentRef: RefObject<HTMLDivElement>;
  confirmationModalRef: RefObject<HTMLDivElement>;
  cancelScrollIndicatorRef: RefObject<HTMLDivElement>;
  cancelModalContentRef: RefObject<HTMLDivElement>;
  bookingSuccessModalContentRef: RefObject<HTMLDivElement>;
  bookingFailureModalContentRef: RefObject<HTMLDivElement>;
}

/**
 * Booking flow actions interface.
 */
interface BookingFlowActions {
  onReasonSelected: () => void;
  closeReasonInstructionsModal: () => void;
  onFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  closeSummaryModal: () => void;
  confirmCloseModal: () => void;
  closeConfirmCloseModal: () => void;
  discardBooking: () => void;
  clearSubmissionError: () => void;
  onTimeslotConflict: () => void;
  onPhoneVerificationExpired: () => Promise<void>;
  confirmCancelBooking: () => void;
  closeCancelBookingModal: () => void;
  isCancelling: boolean;
  closeCancelSuccessModal: () => void;
  closeCancelFailureModal: () => void;
  submissionError: Error | null;
}

interface ProvetFormModalsProps {
  /** Modal state from useProvetModals hook */
  modals: ModalState;
  /** Scroll refs from useProvetScrollManagement hook */
  scrollRefs: ScrollRefs;
  /** Booking flow actions */
  bookingFlowActions: BookingFlowActions;
  /** Current form data */
  formData?: ProvetFormData;
  /** Whether booking is currently being submitted */
  isSubmitting: boolean;
  /** Whether we're loading available appointment times */
  loadingAvailableAppointmentTimes: boolean;
  /** Current active step in the booking flow */
  activeStep: ProvetDialogStep;
  /** Whether to show species incompatibility warning in treatment modal */
  showSpeciesIncompatibleWarning?: boolean;
}

/**
 * Renders all modals used in the Provet booking form.
 *
 * This component centralizes modal rendering to reduce complexity in the main ProvetForm component.
 * It handles 6 different modal types used throughout the booking flow.
 *
 * **Modals Rendered:**
 * 1. **Treatment Modal** - Shows treatment instructions before selection
 * 2. **Summary Modal** - Final review before booking submission
 * 3. **Confirm Close Modal** - Warns before closing summary
 * 4. **Cancel Booking Modal** - Confirms cancellation (on confirmation page)
 * 5. **Cancel Success Modal** - Success feedback after cancellation
 * 6. **Cancel Failure Modal** - Error feedback if cancellation fails
 *
 * **Responsibilities:**
 * - Renders modals conditionally based on active step
 * - Connects modal components to state and handlers
 * - Manages scroll indicators for modals
 * - Handles submission errors
 *
 * @param modals - Modal state (active/inactive, content)
 * @param scrollRefs - Refs for scroll management
 * @param bookingFlowActions - Action handlers for modal interactions
 * @param formData - Current form data for summary display
 * @param isSubmitting - Loading state during submission
 * @param loadingAvailableAppointmentTimes - Loading state for treatment selection
 * @param activeStep - Current step to determine which modals to show
 *
 * @example
 * ```tsx
 * <ProvetFormModals
 *   modals={modals}
 *   scrollRefs={scrollRefs}
 *   bookingFlowActions={{
 *     onFormSubmit: handleSubmit,
 *     closeSummaryModal: closeModal,
 *     // ... other actions
 *   }}
 *   formData={bookingFlowState.formData}
 *   isSubmitting={bookingFlowState.bookingSubmitted}
 *   loadingAvailableAppointmentTimes={bookingFlowState.loadingAvailableAppointmentTimes}
 *   activeStep={bookingFlowState.activeStep}
 * />
 * ```
 */
export const ProvetFormModals = ({
  modals,
  scrollRefs,
  bookingFlowActions,
  formData,
  isSubmitting,
  loadingAvailableAppointmentTimes,
  activeStep,
  showSpeciesIncompatibleWarning = false
}: ProvetFormModalsProps) => {
  // Render modals that are available during form steps
  const renderFormStepModals = () => (
    <>
      {/* Summary Modal - Step 5 (Confirmation) */}
      <ProvetSummaryModal
        active={modals.summary.active}
        formData={formData}
        isSubmitting={isSubmitting}
        submissionError={bookingFlowActions.submissionError}
        onSubmit={bookingFlowActions.onFormSubmit}
        onCancel={bookingFlowActions.closeSummaryModal}
        onClose={bookingFlowActions.confirmCloseModal}
        onRetry={bookingFlowActions.clearSubmissionError}
        onTimeslotConflict={bookingFlowActions.onTimeslotConflict}
        onPhoneVerificationExpired={bookingFlowActions.onPhoneVerificationExpired}
        scrollIndicatorRef={scrollRefs.summaryScrollIndicatorRef}
        contentRef={scrollRefs.summaryModalContentRef}
        modalRef={scrollRefs.confirmationModalRef}
      />

      {/* Confirm Close Modal - Warns before closing summary */}
      <ProvetConfirmCloseModal
        active={modals.confirmClose.active}
        onConfirmClose={bookingFlowActions.discardBooking}
        onCancel={bookingFlowActions.closeConfirmCloseModal}
      />

      {/* Treatment Instructions Modal */}
      <ProvetTreatmentModal
        active={modals.treatment.active}
        title={modals.treatment.title}
        contentHtml={modals.treatment.content}
        isLoading={loadingAvailableAppointmentTimes}
        onConfirm={bookingFlowActions.onReasonSelected}
        onCancel={bookingFlowActions.closeReasonInstructionsModal}
        scrollIndicatorRef={scrollRefs.treatmentScrollIndicatorRef}
        contentRef={scrollRefs.treatmentModalContentRef}
        modalRef={scrollRefs.treatmentModalRef}
        showSpeciesIncompatibleWarning={showSpeciesIncompatibleWarning}
      />
    </>
  );

  // Render modals that are available on confirmation page
  const renderConfirmationPageModals = () => (
    <>
      {/* Cancel Booking Modal */}
      <ProvetCancelBookingModal
        active={modals.cancelBooking.active}
        appointmentDate={formData?.selectedAppointmentTime?.availableTime}
        appointmentTime={formData?.selectedAppointmentTime?.availableTime}
        isLoading={bookingFlowActions.isCancelling}
        onConfirm={bookingFlowActions.confirmCancelBooking}
        onCancel={bookingFlowActions.closeCancelBookingModal}
        contentRef={scrollRefs.cancelModalContentRef}
      />

      {/* Cancel Success Modal */}
      <ProvetCancellationSuccessModal
        active={modals.cancelSuccess.active}
        appointmentDate={modals.cancelSuccess.details?.appointmentDate}
        appointmentTime={modals.cancelSuccess.details?.appointmentTime}
        departmentName={modals.cancelSuccess.details?.departmentName}
        onClose={bookingFlowActions.closeCancelSuccessModal}
        contentRef={scrollRefs.bookingSuccessModalContentRef}
      />

      {/* Cancel Failure Modal */}
      <ProvetCancellationFailureModal
        active={modals.cancelFailure.active}
        appointmentDate={modals.cancelFailure.details?.appointmentDate}
        appointmentTime={modals.cancelFailure.details?.appointmentTime}
        departmentName={modals.cancelFailure.details?.departmentName}
        departmentPhone={modals.cancelFailure.details?.departmentPhone}
        departmentEmail={modals.cancelFailure.details?.departmentEmail}
        onClose={bookingFlowActions.closeCancelFailureModal}
        contentRef={scrollRefs.bookingFailureModalContentRef}
      />
    </>
  );

  return (
    <>
      {/* Modals for form steps (1-5) */}
      {activeStep !== ProvetDialogStep.Confirmed && renderFormStepModals()}

      {/* Modals for confirmation page (step 6) */}
      {activeStep === ProvetDialogStep.Confirmed && renderConfirmationPageModals()}
    </>
  );
};

export default ProvetFormModals;
